export function timeToMinutes(value) {
  if (!value || typeof value !== "string") return null;
  const [hours, minutes] = value.split(":").map(Number);
  if (Number.isNaN(hours) || Number.isNaN(minutes)) return null;
  return hours * 60 + minutes;
}

export function minutesToTime(totalMinutes) {
  if (totalMinutes == null) return "";
  const normalized = ((totalMinutes % 1440) + 1440) % 1440;
  const hours = Math.floor(normalized / 60);
  const minutes = normalized % 60;
  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
}

export function formatTime(value) {
  const minutes = timeToMinutes(value);
  if (minutes == null) return "-";
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  const suffix = hours >= 12 ? "PM" : "AM";
  const hour12 = hours % 12 || 12;
  return `${hour12}:${String(mins).padStart(2, "0")} ${suffix}`;
}

export function timeRangeLabel(start, end) {
  return `${formatTime(start)} - ${formatTime(end)}`;
}

export function getClassMoment(routine, now = new Date()) {
  const current = now.getHours() * 60 + now.getMinutes();
  const start = timeToMinutes(routine.scheduledIn);
  const end = timeToMinutes(routine.scheduledOut);
  if (start == null || end == null) return "later";
  if (current >= start && current <= end) return "running";
  if (current < start) return "upcoming";
  return "past";
}

function compareTime(actual, scheduled, graceMinutes, earlyLabel, onTimeLabel, lateLabel) {
  const actualMinutes = timeToMinutes(actual);
  const scheduledMinutes = timeToMinutes(scheduled);

  if (actualMinutes == null || scheduledMinutes == null) {
    return {
      status: "",
      lateMinutes: 0,
      earlyMinutes: 0,
      differenceMinutes: 0
    };
  }

  const differenceMinutes = actualMinutes - scheduledMinutes;
  const absoluteDifference = Math.abs(differenceMinutes);

  if (absoluteDifference <= graceMinutes) {
    return {
      status: onTimeLabel,
      lateMinutes: 0,
      earlyMinutes: 0,
      differenceMinutes
    };
  }

  if (differenceMinutes > 0) {
    return {
      status: lateLabel,
      lateMinutes: differenceMinutes,
      earlyMinutes: 0,
      differenceMinutes
    };
  }

  return {
    status: earlyLabel,
    lateMinutes: 0,
    earlyMinutes: Math.abs(differenceMinutes),
    differenceMinutes
  };
}

export function calculateLogMetrics(log, settings = {}) {
  const entryGraceMinutes = Number(settings.entryGraceMinutes ?? 5);
  const exitGraceMinutes = Number(settings.exitGraceMinutes ?? 5);
  const entry = compareTime(
    log.actualIn,
    log.scheduledIn,
    entryGraceMinutes,
    "Early Entry",
    "On Time Entry",
    "Late Entry"
  );
  const exit = compareTime(
    log.actualOut,
    log.scheduledOut,
    exitGraceMinutes,
    "Early Exit",
    "On Time Exit",
    "Late Exit"
  );

  return {
    entryDifferenceMinutes: entry.differenceMinutes,
    exitDifferenceMinutes: exit.differenceMinutes,
    entryStatus: entry.status,
    exitStatus: exit.status,
    lateEntryMinutes: entry.lateMinutes,
    earlyEntryMinutes: entry.earlyMinutes,
    lateExitMinutes: exit.lateMinutes,
    earlyExitMinutes: exit.earlyMinutes
  };
}

export function entryDifferenceLabel(log) {
  if (!log?.actualIn) return "-";
  const diff = Number(log.entryDifferenceMinutes || 0);
  if (diff === 0) return "0 min";
  return diff > 0 ? `+${diff} min` : `${diff} min`;
}

export function exitDifferenceLabel(log) {
  if (!log?.actualOut) return "-";
  const diff = Number(log.exitDifferenceMinutes || 0);
  if (diff === 0) return "0 min";
  return diff > 0 ? `+${diff} min` : `${diff} min`;
}

function minuteText(minutes) {
  const value = Math.abs(Number(minutes || 0));
  return `${value} min${value === 1 ? "" : "s"}`;
}

export function timingStatusLabel(log, kind = "entry") {
  const status = kind === "exit" ? log?.exitStatus : log?.entryStatus;
  if (!status) return "";
  if (status.includes("On Time")) return "On Time";
  if (status.includes("Late")) {
    const minutes = kind === "exit" ? log?.lateExitMinutes : log?.lateEntryMinutes;
    return `Late by ${minuteText(minutes)}`;
  }
  if (status.includes("Early")) {
    const minutes = kind === "exit" ? log?.earlyExitMinutes : log?.earlyEntryMinutes;
    return `Early by ${minuteText(minutes)}`;
  }
  return status;
}
